import { useState } from 'react';
import { LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useSchool } from '@/contexts/SchoolContext';
import { useNotifications } from '@/components/notifications/NotificationSystem';
import {
  getIncidents,
  linkFileToIncident,
  linkRecordToIncident,
} from '@/services/incidents/IncidentService';

function incidentLabel(incident) {
  const names = (incident.participants || []).map((person) => person.studentName).filter(Boolean);
  const people = names.slice(0, 3).join(', ');
  const more = names.length > 3 ? ` +${names.length - 3}` : '';
  return incident.summary || `${people}${more}` || `Incident ${incident.id.slice(0, 6)}`;
}

export default function IncidentLinkAction({ reference, kind = 'file', compact = false }) {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const [open, setOpen] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);

  const openPicker = async () => {
    setOpen(true);
    setLoading(true);
    try {
      setIncidents(await getIncidents(schoolId, { openOnly: true }));
    } catch (error) {
      console.error('Could not load open incidents:', error);
      showError('Could not load open incidents');
    } finally {
      setLoading(false);
    }
  };

  const link = async (incidentId) => {
    setLinkingId(incidentId);
    try {
      const action = kind === 'file' ? linkFileToIncident : linkRecordToIncident;
      await action(schoolId, incidentId, reference, user);
      showSuccess('Linked to incident');
      setOpen(false);
    } catch (error) {
      console.error('Could not link incident reference:', error);
      showError('Could not link this item to the incident');
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <>
      <button
        type="button"
        className={compact ? 'btn btn-ghost btn-xs btn-circle' : 'btn btn-ghost btn-sm gap-2'}
        onClick={openPicker}
        title="Link to incident…"
        aria-label="Link to incident"
      >
        <LinkIcon className="w-4 h-4" />
        {!compact && 'Link to incident…'}
      </button>

      {open && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-lg">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-lg">Link to an open incident</h3>
                <p className="text-sm text-base-content/60 mt-1">
                  The original item stays where it is and keeps its current permissions.
                </p>
              </div>
              <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={() => setOpen(false)}>
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-5 space-y-2 max-h-80 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8"><span className="loading loading-spinner" /></div>
              ) : incidents.length === 0 ? (
                <div className="rounded-xl bg-base-200 p-5 text-sm text-base-content/60">
                  There are no open incidents to link to.
                </div>
              ) : incidents.map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  className="w-full rounded-xl border border-base-300 p-3 text-left hover:border-primary hover:bg-primary/5 transition-colors"
                  onClick={() => link(incident.id)}
                  disabled={Boolean(linkingId)}
                >
                  <div className="font-medium">{incidentLabel(incident)}</div>
                  <div className="text-xs text-base-content/60 mt-1">
                    {(incident.participants || []).length} {(incident.participants || []).length === 1 ? 'learner' : 'learners'}
                    {linkingId === incident.id ? ' · Linking…' : ''}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setOpen(false)}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </>
  );
}
